'use client';

import { useTranslations } from 'next-intl';
import {
  BodyDefault,
  Field,
  Input,
  LabelDefault,
  MetaDefault,
} from '@terra/ui-design-system';
import { VStack } from '@terra/ui-styled-system';
import { automationId } from '@terra/shared-utils';
import {
  canCreateFromHars,
  getHarUrlHost,
  getHarOperationKey,
  type HarCreateGroup,
} from '../../utils/reassociate-har-entries/har-reassociate-identity';
import { REQUESTS_REASSOCIATE_AUTOMATION_IDS } from '../tabs/requestsReassociateAutomationIds';

type ReassociateCreateFromHarsFieldsProps = {
  groups: readonly HarCreateGroup[];
  pathsByGroupId: Record<string, string>;
  createCollision: 'existing' | undefined;
  onPathChange: (groupId: string, path: string) => void;
};

export function ReassociateCreateFromHarsFields({
  groups,
  pathsByGroupId,
  createCollision,
  onPathChange,
}: ReassociateCreateFromHarsFieldsProps) {
  const t = useTranslations('proxy.requests.reassociate');
  const firstRow = groups[0]?.rows[0];
  const operations = [
    ...new Set(
      groups
        .map((group) => {
          const row = group.rows[0];

          return row ? getHarOperationKey(row) : undefined;
        })
        .filter((operation): operation is string => Boolean(operation)),
    ),
  ];
  const sharedOperation = operations.length === 1 ? operations[0] : undefined;

  return (
    <VStack gap="3" alignItems="stretch">
      {firstRow ? (
        <VStack gap="1" alignItems="stretch">
          <MetaDefault color="fg.muted">
            {t('method')}: {firstRow.method}
          </MetaDefault>
          <MetaDefault color="fg.muted">
            {t('host')}: {getHarUrlHost(firstRow.url)}
          </MetaDefault>
          {sharedOperation ? (
            <MetaDefault color="fg.muted">
              {t('operation')}: {sharedOperation}
            </MetaDefault>
          ) : null}
        </VStack>
      ) : null}
      {groups.map((group) => {
        const row = group.rows[0];
        const path = pathsByGroupId[group.id] ?? '';
        const canCreate = canCreateFromHars({ path, rows: group.rows });
        const operation = row ? getHarOperationKey(row) : undefined;

        return (
          <Field.Root key={group.id}>
            {sharedOperation || !operation ? null : (
              <MetaDefault color="fg.muted">
                {t('operation')}: {operation}
              </MetaDefault>
            )}
            <Field.Label>
              <LabelDefault>{t('pathLabel')}</LabelDefault>
            </Field.Label>
            <Input
              size="sm"
              value={path}
              onChange={(event) => onPathChange(group.id, event.target.value)}
              {...automationId(
                REQUESTS_REASSOCIATE_AUTOMATION_IDS.pathField,
                group.id,
              )}
            />
            {!canCreate ? (
              <Field.HelperText>
                <MetaDefault color="fg.error">
                  {t('pathDoesNotMatch')}
                </MetaDefault>
              </Field.HelperText>
            ) : null}
          </Field.Root>
        );
      })}
      {createCollision ? (
        <BodyDefault color="fg.error">{t('createCollision')}</BodyDefault>
      ) : null}
    </VStack>
  );
}
